import { useEffect, useState } from 'react';

import { ErroDeRede, carregarEstocagem } from '../nucleo/api.js';
import {
  diasJaNoRotulo,
  eixoCurto,
  ganhoAparente,
  medicoesDistintas,
  parNoOutroEixo,
  porEstudo,
} from '../nucleo/estocagem.js';
import { NOME_DA_ESPECIE } from '../nucleo/especies.js';
import { enxuto, plural, porcento } from '../nucleo/numero.js';

/**
 * A base de evidência da estocagem, inteira e numa tela só.
 *
 * Por que ela existe separada da ficha de cada material: a ficha de
 * `suino_terminacao` mostra as séries daquela categoria e nada diz sobre de
 * onde vieram. Mas as séries NÃO são por categoria — são por ensaio. A mesma
 * medição alemã de chorume serve três categorias do catálogo, porque o dejeto
 * é o mesmo e o estudo não separou. Quem vê três fichas tem a impressão de
 * três evidências independentes, e há uma.
 *
 * Daí a regra desta tela: **ela conta medições e nomeia estudos, nunca linhas.**
 * São 21 linhas no catálogo e 11 medições de 5 estudos. Contar 21 inflaria a
 * base de evidência em quase o dobro — e uma plataforma cuja promessa é "o
 * número tem fonte" não pode errar justamente no tamanho da fonte.
 *
 * O segundo trabalho dela é desarmar a armadilha do eixo, e a única forma
 * honesta de fazer isso é mostrar os dois números do MESMO frasco lado a lado:
 * guardado a 5 °C por 90 dias, o dejeto *aparenta* ganhar 9,6% de metano por
 * kg de SV e perde 11,6% por tonelada de material. Não melhorou: o
 * denominador encolheu. Nenhuma ressalva em prosa convence tanto quanto os
 * dois números juntos — por isso o par vem no mesmo cartão, não numa nota.
 *
 * Para quem é: o produtor lê a afirmação do topo e o recado do fim e já sabe o
 * que fazer (alimentar com o material mais fresco que conseguir). O consultor
 * desce a lista e cita o estudo.
 */
export function Estocagem() {
  const [base, setBase] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let vivo = true;
    carregarEstocagem()
      .then((b) => {
        if (vivo) setBase(b);
      })
      .catch((e) => {
        if (vivo) setErro(e);
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <SemBase erro={erro} />;
  if (!base) return <Carregando />;

  const medicoes = medicoesDistintas(base.series);
  const estudos = porEstudo(medicoes);
  const enganam = medicoes.filter((m) => m.efeito.eixo_engana);

  return (
    <div className="empilha-largo">
      <section className="bloco">
        <h2 className="simulador__titulo">Perda por estocagem</h2>
        <p className="afirmacao">{base.afirmacao}</p>
        <Contagem base={base} medicoes={medicoes} estudos={estudos} />
      </section>

      {enganam.length > 0 ? <Armadilha enganam={enganam} medicoes={medicoes} /> : null}

      {estudos.map(([fonte, doEstudo]) => (
        <section className="bloco" key={fonte}>
          <h3 className="rotulo-secao">
            {plural(doEstudo.length, 'medição', 'medições')} deste estudo
          </h3>
          <p className="serie__fonte">{fonte}</p>
          <div className="empilha">
            {doEstudo.map((m) => (
              <Medicao key={m.chave} medicao={m} medicoes={medicoes} />
            ))}
          </div>
        </section>
      ))}

      <section className="bloco">
        <h3 className="rotulo-secao">O que fazer com isso</h3>
        <p className="afirmacao">{base.recado}</p>
      </section>
    </div>
  );
}

/**
 * A contagem honesta, dita em voz alta.
 *
 * As 21 linhas aparecem na frase de propósito. Esconder a diferença entre
 * linha e medição não tornaria o número mais bonito — tornaria o leitor
 * incapaz de conferir, porque ele encontraria 21 séries ao somar as fichas e
 * não saberia por que a conta não fecha.
 */
function Contagem({ base, medicoes, estudos }) {
  const reusadas = medicoes.filter((m) => m.usos.length > 1);
  const linhas = medicoes.reduce((n, m) => n + m.usos.length, 0);

  return (
    <>
      <p className="ficha__linha">
        <span className="numero-grande">{base.total_de_series}</span>{' '}
        <span className="unidade">
          {base.total_de_series === 1 ? 'medição' : 'medições'} de{' '}
          {plural(estudos.length, 'estudo independente', 'estudos independentes')}, em{' '}
          {base.especies.map((e) => NOME_DA_ESPECIE[e] ?? e).join(', ')}
        </span>
      </p>

      {reusadas.length > 0 ? (
        <p className="secundario">
          Essas {base.total_de_series} medições aparecem {linhas} vezes no catálogo, porque{' '}
          {reusadas.length === 1 ? 'uma delas serve' : `${reusadas.length} delas servem`} mais de
          uma categoria: o dejeto é o mesmo e o estudo não separou.{' '}
          <strong>
            São {base.total_de_series} evidências, não {linhas}.
          </strong>
        </p>
      ) : null}
    </>
  );
}

/**
 * O aviso do eixo, com o par do mesmo frasco dentro dele.
 *
 * Fica acima da lista porque é o erro de leitura mais provável desta tela:
 * quem bate o olho num "ganho de 9,6%" e vai embora conclui o contrário do que
 * a evidência diz.
 */
function Armadilha({ enganam, medicoes }) {
  return (
    <section className="bloco">
      <h3 className="rotulo-secao">Antes de ler os números: o eixo</h3>
      <p className="secundario">
        O mesmo ensaio dá sinais opostos conforme o que está no denominador.
        Medido <strong>por kg de SV</strong>, mede concentração; medido{' '}
        <strong>por tonelada de material</strong>, mede o que o produtor
        bombeia. Guardar dejeto derruba os sólidos voláteis — e dividir metano
        por um denominador menor faz o número subir sem que haja mais metano.
      </p>
      <div className="empilha">
        {enganam.map((m) => {
        const par = parNoOutroEixo(m, medicoes);
        return (
          <div className="par-de-eixos" key={m.chave}>
            <p className="serie__titulo">
              {m.efeito.de} → {m.efeito.para}
            </p>
            <ul className="par-de-eixos__lista">
              <li>
                <span className="terciario">{eixoCurto(m.efeito.eixo)}:</span>{' '}
                <strong className="serie__ganho">
                  aparente ganho de {porcento(Math.abs(m.efeito.perda_pct), 1)}
                </strong>
              </li>
              {par ? (
                <li>
                  <span className="terciario">{eixoCurto(par.efeito.eixo)}:</span>{' '}
                  <strong className="serie__perda">
                    perda de {porcento(par.efeito.perda_pct, 1)}
                  </strong>
                </li>
              ) : null}
            </ul>
            {par ? (
              <p className="ressalva ressalva--grave">
                É o <strong>mesmo frasco</strong>, lido de duas formas. O dejeto não melhorou
                guardado: o denominador encolheu. A leitura que vale para quem vai bombear o
                material é a de baixo.
              </p>
            ) : (
              <p className="ressalva ressalva--grave">
                Esta medição não tem par no outro eixo publicado, então o ganho aparente não pode
                ser contraposto com um número desta mesma base. Leia-o como concentração, nunca
                como mais metano.
              </p>
            )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Uma medição, com todos os números que a tornam conferível. */
function Medicao({ medicao, medicoes }) {
  const e = medicao.efeito;
  const ganho = ganhoAparente(e);
  const par = parNoOutroEixo(medicao, medicoes);

  return (
    <article className={`serie ${e.eixo_engana ? 'serie--engana' : ''}`}>
      <h4 className="serie__titulo">
        {e.de} → {e.para}
        {e.dias != null && !diasJaNoRotulo(e) ? (
          <span className="terciario"> ({e.dias} dias)</span>
        ) : null}
      </h4>

      <p className="serie__numeros">
        <span>
          {enxuto(e.valor_de, 2)} → {enxuto(e.valor_para, 2)}{' '}
          <span className="terciario">{e.unidade}</span>
        </span>
        <strong className={ganho ? 'serie__ganho' : 'serie__perda'}>
          {ganho
            ? `aparente ganho de ${porcento(Math.abs(e.perda_pct), 1)}`
            : `perda de ${porcento(e.perda_pct, 1)}`}
        </strong>
      </p>

      <p className="terciario serie__eixo">
        Eixo: {eixoCurto(e.eixo)}
        {e.perda_e_piso ? ' · este é o piso, a perda real pode ser maior' : ''}
        {par ? ` · o mesmo ensaio ${eixoCurto(par.efeito.eixo)} dá ${
          ganhoAparente(par.efeito)
            ? `aparente ganho de ${porcento(Math.abs(par.efeito.perda_pct), 1)}`
            : `perda de ${porcento(par.efeito.perda_pct, 1)}`
        }` : ''}
      </p>

      {e.eixo_engana ? (
        <p className="ressalva ressalva--grave">
          <strong>Este eixo engana.</strong> {e.observacao}
        </p>
      ) : e.observacao ? (
        <p className="terciario">{e.observacao}</p>
      ) : null}

      <p className="terciario">
        Vale para{' '}
        {plural(medicao.usos.length, 'categoria', 'categorias')}:{' '}
        {medicao.usos.map((u) => u.rotulo).join(' · ')}
      </p>
    </article>
  );
}

function Carregando() {
  return (
    <p className="secundario" role="status">
      Carregando a base de evidência…
    </p>
  );
}

/**
 * O erro, dito de forma que o leitor saiba o que fazer.
 *
 * Esta tela é toda feita de uma rota só: sem ela não há meia tela a mostrar, e
 * fingir que há (com um esqueleto vazio, por exemplo) daria a impressão de que
 * não existe evidência nenhuma, que é o contrário da verdade.
 */
function SemBase({ erro }) {
  const offline = erro instanceof ErroDeRede && erro.offline;

  return (
    <section className="bloco" role="alert">
      <h2 className="simulador__titulo">Perda por estocagem</h2>
      <p className="secundario">
        {offline
          ? 'Você está sem conexão e esta tela ainda não guarda a base de evidência para ler offline. Abra de novo quando houver rede.'
          : 'Não deu para carregar a base de evidência agora. O dado existe no servidor; o que falhou foi a busca.'}
      </p>
      <p className="ressalva">
        A perda por estocagem continua valendo mesmo sem esta tela: alimente o biodigestor com o
        material mais fresco que conseguir.
      </p>
    </section>
  );
}
